import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, useScroll, useSpring, MotionConfig } from "framer-motion";
import { Toaster } from "sonner";
import "@/App.css";

import { ThemeToggle } from "@/components/ThemeToggle";
import { Hero } from "@/components/sections/Hero";
import { Waiting } from "@/components/sections/Waiting";
import { Scribe } from "@/components/sections/Scribe";
import { Objects } from "@/components/sections/Objects";
import { Collapse, CollapseOutro } from "@/components/sections/Collapse";
import { Telegram } from "@/components/sections/Telegram";
import { Gained } from "@/components/sections/Gained";
import { Lost } from "@/components/sections/Lost";
import { Vanished } from "@/components/sections/Vanished";
import { AInow } from "@/components/sections/AInow";
import { Turn } from "@/components/sections/Turn";
import { LetterForm } from "@/components/sections/LetterForm";
import { Footer } from "@/components/sections/Footer";
import { NotFound } from "@/components/sections/NotFound";

const Essay = () => (
    <main id="top" className="relative z-10">
        <Hero />
        <Waiting />
        <Scribe />
        <Objects />
        <Collapse />
        <CollapseOutro />
        <Telegram />
        <Gained />
        <Lost />
        <Vanished />
        <AInow />
        <Turn />
        <LetterForm />
        <Footer />
    </main>
);

function App() {
    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

    useEffect(() => {
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce || window.location.search.includes("nolenis")) return;
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    return (
        <MotionConfig reducedMotion="user">
            <BrowserRouter>
                <motion.div
                    style={{ scaleX: progress }}
                    className="fixed left-0 top-0 z-50 h-[2px] w-full origin-left bg-wax"
                    aria-hidden="true"
                />

                <header className="fixed left-0 top-0 z-40 flex w-full items-center justify-between px-6 py-5 md:px-10">
                    <a
                        href="#top"
                        data-testid="brand-mark"
                        className="font-serif text-lg tracking-tight text-ink transition-colors hover:text-wax"
                    >
                        patr<span className="text-wax">.</span>ai
                    </a>
                    <ThemeToggle />
                </header>

                <Routes>
                    <Route path="/" element={<Essay />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>

                <Toaster
                    position="bottom-center"
                    toastOptions={{
                        style: {
                            background: "var(--surface)",
                            color: "var(--ink)",
                            border: "1px solid var(--rule)",
                            fontFamily: "'EB Garamond', serif",
                            fontSize: "1.05rem",
                        },
                    }}
                />
            </BrowserRouter>
        </MotionConfig>
    );
}

export default App;
